
import React from 'react';
import Box from '../../components/Common/Box';
import Disclaimer from '../../components/Common/Disclaimer';

interface MobilePepitesProps {
    data: any;
    activeTab: string;
    setActiveTab: (tab: any) => void;
    filters: any;
    setFilters: (filters: any) => void;
    opportunities: any[];
    sellSignals: any[];
    filteredUniverse: any[];
    sentiment: string;
    sentimentColors: any;
    navigate: (path: string) => void;
}

const MobilePepites: React.FC<MobilePepitesProps> = ({
    data,
    activeTab,
    setActiveTab,
    filters,
    setFilters,
    opportunities,
    sellSignals,
    filteredUniverse,
    sentiment,
    sentimentColors,
    navigate
}) => {
    return (
        <div style={{ padding: '16px', maxWidth: '100vw', overflowX: 'hidden' }}>
            <Disclaimer type="investment" />

            {/* Mobile Summary Grid */}
            <Box>
                <div style={styles.summaryGrid}>
                    <div style={styles.summaryItem}>
                        <span style={styles.summaryLabel}>🚀 Pépites</span>
                        <span style={styles.summaryValue}>{opportunities.length}</span>
                    </div>
                    <div style={styles.summaryItem}>
                        <span style={styles.summaryLabel}>📈 Marché</span>
                        <span style={{
                            ...styles.badge,
                            backgroundColor: sentimentColors[sentiment].bg,
                            color: sentimentColors[sentiment].color,
                            fontSize: '11px',
                            padding: '2px 8px'
                        }}>
                            {sentiment === 'BULLISH' ? '🟢 Haussier' : sentiment === 'BEARISH' ? '🔴 Baissier' : '⚪ Neutre'}
                        </span>
                    </div>
                </div>
            </Box>

            {/* Scrollable Tabs */}
            <div style={styles.tabContainer}>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'opportunities' ? styles.tabActive : {}) }}
                    onClick={() => setActiveTab('opportunities')}
                >
                    🔥 Pépites ({opportunities.length})
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'sell' ? styles.tabActiveSell : {}) }}
                    onClick={() => setActiveTab('sell')}
                >
                    📉 Alertes ({sellSignals.length})
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'universe' ? styles.tabActiveUniverse : {}) }}
                    onClick={() => setActiveTab('universe')}
                >
                    🌍 Univers
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'info' ? styles.tabActiveInfo : {}) }}
                    onClick={() => setActiveTab('info')}
                >
                    ℹ️ Stratégie
                </button>
            </div>

            {/* Opportunities (Cards) */}
            {activeTab === 'opportunities' && (
                <div style={styles.cardsGrid}>
                    {opportunities.length === 0 ? (
                        <Box>
                            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>
                                <p style={{ fontSize: '14px' }}>Aucune pépite détectée actuellement.</p>
                            </div>
                        </Box>
                    ) : (
                        opportunities.map((opp) => (
                            <Box key={opp.symbole}>
                                <div style={styles.card}>
                                    <div style={styles.cardHeader}>
                                        <div onClick={() => navigate(`/data?symbol=${opp.symbole}`)} style={{ cursor: 'pointer' }}>
                                            <span style={styles.symbolLink}>{opp.symbole}</span>
                                            <p style={styles.companyName}>{opp.nom_societe}</p>
                                        </div>
                                        <div style={styles.scoreContainer}>
                                            <div style={{ ...styles.scoreBadge, backgroundColor: '#7c3aed' }}>
                                                {opp.score}
                                            </div>
                                        </div>
                                    </div>

                                    <div style={styles.priceGrid}>
                                        <div style={styles.priceItem}>
                                            <span style={styles.priceLabel}>Prix</span>
                                            <span style={styles.priceValue}>{opp.entry_price.toLocaleString()}</span>
                                        </div>
                                        <div style={styles.priceItem}>
                                            <span style={styles.priceLabel}>CCI</span>
                                            <span style={{ ...styles.priceValue, color: (opp.cci || 0) > 100 ? '#16a34a' : 'var(--text-main)' }}>
                                                {opp.cci?.toFixed(0)}
                                            </span>
                                        </div>
                                        <div style={styles.priceItem}>
                                            <span style={styles.priceLabel}>Pression</span>
                                            <span style={{ ...styles.priceValue, color: (opp.current_pression || 0) > 1.2 ? '#16a34a' : 'var(--text-muted)' }}>
                                                {opp.current_pression?.toFixed(1) || '-'}
                                            </span>
                                        </div>
                                        <div style={styles.priceItem}>
                                            <span style={styles.priceLabel}>Z-Score</span>
                                            <span style={{ ...styles.priceValue, color: (opp.z_score || 0) > 2.0 ? '#16a34a' : 'var(--text-main)' }}>
                                                {opp.z_score?.toFixed(1)}σ
                                            </span>
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                        {opp.volume_spike && opp.volume_spike > 1.5 && (
                                            <span style={styles.miniBadgeWarning}>
                                                ⚡ Vol x{opp.volume_spike.toFixed(1)}
                                            </span>
                                        )}
                                        <span style={styles.miniBadgeSuccess}>
                                            🟢 Trend {opp.trend_days !== undefined ? `${opp.trend_days}j` : 'OK'}
                                        </span>
                                    </div>

                                    <p style={styles.justification}>{opp.justification}</p>
                                </div>
                            </Box>
                        ))
                    )}
                </div>
            )}

            {/* Sell Signals */}
            {activeTab === 'sell' && (
                <div style={styles.cardsGrid}>
                    {sellSignals.length === 0 ? (
                        <Box>
                            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>
                                <p style={{ fontSize: '14px' }}>Aucune alerte baissière.</p>
                            </div>
                        </Box>
                    ) : (
                        sellSignals.map((signal: any) => (
                            <Box key={signal.symbole}>
                                <div style={styles.card}>
                                    <div style={styles.cardHeader}>
                                        <div onClick={() => navigate(`/data?symbol=${signal.symbole}`)} style={{ cursor: 'pointer' }}>
                                            <span style={styles.symbolLink}>{signal.symbole}</span>
                                            <p style={styles.companyName}>{signal.nom_societe}</p>
                                        </div>
                                        <div style={{ ...styles.scoreBadge, backgroundColor: signal.signal_strength === 'STRONG' ? '#dc2626' : '#f59e0b' }}>
                                            {signal.signal_strength === 'STRONG' ? '🔴' : '🟡'}
                                        </div>
                                    </div>

                                    <div style={styles.priceGrid}>
                                        <div style={styles.priceItem}>
                                            <span style={styles.priceLabel}>Prix</span>
                                            <span style={styles.priceValue}>{signal.current_price?.toLocaleString()}</span>
                                        </div>
                                        <div style={styles.priceItem}>
                                            <span style={styles.priceLabel}>P&L</span>
                                            <span style={{ ...styles.priceValue, color: signal.unrealized_pnl_pct >= 0 ? '#16a34a' : '#dc2626' }}>
                                                {signal.unrealized_pnl_pct?.toFixed(1)}%
                                            </span>
                                        </div>
                                        <div style={styles.priceItem}>
                                            <span style={styles.priceLabel}>CCI</span>
                                            <span style={{ ...styles.priceValue, color: signal.cci > 100 ? '#16a34a' : '#dc2626' }}>
                                                {signal.cci?.toFixed(0)}
                                            </span>
                                        </div>
                                    </div>

                                    <p style={styles.justification}>{signal.justification}</p>

                                    <div style={styles.actionRow}>
                                        <span style={{ ...styles.actionBadge, backgroundColor: '#dc2626' }}>
                                            VENDRE / ALLÉGER
                                        </span>
                                    </div>
                                </div>
                            </Box>
                        )
                    ))}
                </div>
            )}

            {/* Universe (Cards on mobile) */}
            {activeTab === 'universe' && (
                <div>
                    <div style={{ marginBottom: 16 }}>
                        <input
                            type="text"
                            placeholder="🔍 Chercher une action..."
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            style={styles.searchInput}
                        />
                        <div style={{ display: 'flex', gap: 6, marginTop: 10, overflowX: 'auto', paddingBottom: 4 }}>
                            <button
                                onClick={() => setFilters({ ...filters, cciBreakout: !filters.cciBreakout })}
                                style={{ ...styles.filterChip, ...(filters.cciBreakout ? styles.filterChipActive : {}) }}
                            >
                                CCI {'>'} 100
                            </button>
                            <button
                                onClick={() => setFilters({ ...filters, obvBullish: !filters.obvBullish })}
                                style={{ ...styles.filterChip, ...(filters.obvBullish ? styles.filterChipActive : {}) }}
                            >
                                OBV ↗️
                            </button>
                        </div>
                    </div>

                    <div style={styles.cardsGrid}>
                        {filteredUniverse.slice(0, 20).map((stock: any) => (
                            <Box key={stock.symbole}>
                                <div style={{ 
                                    ...styles.card, 
                                    borderLeft: stock.is_opportunity ? '4px solid #7c3aed' : '1px solid var(--border-color)' 
                                }} onClick={() => navigate(`/data?symbol=${stock.symbole}`)}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <span style={{ fontWeight: 'bold', fontSize: '15px' }}>{stock.symbole}</span>
                                            {stock.is_opportunity && <span style={{ marginLeft: 6, fontSize: '10px' }}>🚀</span>}
                                        </div>
                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{stock.cours_actuel?.toLocaleString()}</div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 15, marginTop: 8, fontSize: '12px', color: 'var(--text-muted)' }}>
                                        <span>CCI: <b style={{ color: stock.cci > 100 ? '#16a34a' : 'inherit' }}>{stock.cci?.toFixed(0)}</b></span>
                                        <span>Pression: <b style={{ color: stock.current_pression > 1 ? '#16a34a' : 'inherit' }}>{stock.current_pression?.toFixed(1)}</b></span>
                                        <span>Trend: {stock.trend_status === 'active' ? '🟢' : '⚪'}</span>
                                    </div>
                                </div>
                            </Box>
                        ))}
                    </div>
                </div>
            )}

            {/* Info */}
            {activeTab === 'info' && (
                <Box>
                    <div style={{ padding: 16 }}>
                        <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>💎 Stratégie "Trend Following"</h3>
                        <div style={{ fontSize: '13px', lineHeight: '1.5', color: 'var(--text-main)' }}>
                            <p>Détection d'accélération haussière validée par le carnet d'ordres.</p>
                            
                            <h4 style={{ marginTop: 15, fontSize: '14px', color: '#16a34a' }}>🟢 Entrée</h4>
                            <p>CCI {'>'} 100 + Pression {'>'} 1.2 + Liquidité valide.</p>

                            <h4 style={{ marginTop: 15, fontSize: '14px', color: '#dc2626' }}>🔴 Sortie</h4>
                            <p>CCI {'<'} 50 ou Pression {'<'} 0.8.</p>
                        </div>
                    </div>
                </Box>
            )}
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    summaryGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '12px' },
    summaryItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' },
    summaryLabel: { fontSize: '11px', color: 'var(--text-muted)', marginBottom: 2 },
    summaryValue: { fontSize: '18px', fontWeight: 'bold', color: 'var(--text-heading)' },
    badge: { padding: '4px 10px', borderRadius: 20, fontWeight: '600' },
    tabContainer: { display: 'flex', gap: 6, marginBottom: 16, overflowX: 'auto', paddingBottom: 6, scrollbarWidth: 'none' },
    tab: { padding: '8px 14px', border: 'none', borderRadius: 20, fontSize: '12px', fontWeight: '600', cursor: 'pointer', backgroundColor: 'var(--bg-box)', color: 'var(--text-main)', whiteSpace: 'nowrap' },
    tabActive: { backgroundColor: '#7c3aed', color: 'white' },
    tabActiveSell: { backgroundColor: '#dc2626', color: 'white' },
    tabActiveUniverse: { backgroundColor: '#2563eb', color: 'white' },
    tabActiveInfo: { backgroundColor: 'var(--bg-input)', color: 'var(--text-heading)' },
    cardsGrid: { display: 'flex', flexDirection: 'column', gap: 12 },
    card: { padding: '14px', backgroundColor: 'var(--bg-box)', borderRadius: 12, border: '1px solid var(--border-color)' },
    cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
    symbolLink: { fontSize: '16px', fontWeight: 'bold', color: '#2563eb' },
    companyName: { fontSize: '11px', color: 'var(--text-muted)', margin: '2px 0 0 0' },
    scoreContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
    scoreBadge: { width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: '13px' },
    priceGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12, padding: '10px', backgroundColor: 'var(--bg-input)', borderRadius: 8 },
    priceItem: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
    priceLabel: { fontSize: '9px', color: 'var(--text-muted)', marginBottom: 2, textTransform: 'uppercase' },
    priceValue: { fontSize: '12px', fontWeight: 'bold', color: 'var(--text-heading)' },
    justification: { fontSize: '12px', color: 'var(--text-muted)', margin: 0, fontStyle: 'italic', lineHeight: '1.4' },
    actionRow: { display: 'flex', justifyContent: 'center', marginTop: 12 },
    actionBadge: { padding: '6px 16px', borderRadius: 6, fontSize: '11px', fontWeight: 'bold', color: 'white' },
    searchInput: { width: '100%', padding: '10px 15px', borderRadius: 20, border: '1px solid var(--border-color)', fontSize: '14px', outline: 'none', backgroundColor: 'var(--bg-box)', color: 'var(--text-main)' },
    filterChip: { padding: '6px 12px', borderRadius: 20, border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-box)', fontSize: '11px', cursor: 'pointer', color: 'var(--text-muted)', whiteSpace: 'nowrap' },
    filterChipActive: { backgroundColor: 'rgba(37, 99, 235, 0.1)', borderColor: '#2563eb', color: '#2563eb', fontWeight: 'bold' },
    miniBadgeWarning: { fontSize: '10px', backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#d97706', padding: '2px 6px', borderRadius: 4, fontWeight: 600 },
    miniBadgeSuccess: { fontSize: '10px', backgroundColor: 'rgba(22, 163, 74, 0.1)', color: '#16a34a', padding: '2px 6px', borderRadius: 4, fontWeight: 600 },
};

export default MobilePepites;
